import PropTypes from 'prop-types';
import {observer, PropTypes as MobXPropTypes} from 'mobx-react';
import React from 'react';

import analytics from 'analytics.js';
import autobind from 'common/decorators/autobind.js';

import BookNowButton from 'core/components/BookNowButton';
import IntegratedAvailabilityCallout from 'core/components/IntegratedAvailabilityCallout.js';
import CollapsibleNumber from 'core/components/CollapsibleNumber.js';
import Link from 'core/components/Link.js';
import myHealthFirst from 'bookings/my_health_first.js';
import {
    HICAPS,
    TELEHEALTH,
    PRACTICE_LOC_CONTACT_TYPE,
    PROFILE_CONTACT_TYPE,
    TOOLTIP_TELEHEALTH_TEXT,
    TOOLTIP_HICAPS_TEXT,
    TOOLTIP_FEATURED_TEXT,
    DIRECTORY_SEARCH_RESULT_CARD_LINK,
    BANNER_AD_CARD,
    BOOK_NOW_BTN_MH1_PRIMARY,
    PRACTICE_LOCATION_CARD,
    PRACTICE_GROUP_CARD,
    PROFILE_CARD,
    AVATAR_SIZE_LARGE,
    AVATAR_SIZE_DEFAULT,
} from 'core/constants.js';
import {Mh1BookingApi, getBookingAvailability} from 'core/services/Mh1Service';
import Icon from 'core/components/Icon.js';

import {
    Card,
    Chip,
    Pill,
    Icon as IconHcl,
    Tooltip,
} from '@HealthShareAU/hs-component-library';
import {openLinkInNewTab, browserCheckForIE11} from 'core/utils.js';
import {DirectoryStoreContext} from 'core/stores/RootStore.js';

export default class BannerAd extends React.Component {
    static contextType = DirectoryStoreContext;
    static propTypes = {
        bannerType: PropTypes.string,
        bookingsEnabled: MobXPropTypes.arrayOrObservableArrayOf(
            PropTypes.string,
        ),
        customBookingDataUrl: PropTypes.string,
        displayPhoneNumber: PropTypes.bool,
        hasMedicalSpecialty: PropTypes.bool.isRequired,
        headline: PropTypes.string.isRequired,
        healthFund: PropTypes.shape({
            id: PropTypes.number,
            showRatings: PropTypes.bool,
        }),
        id: PropTypes.number.isRequired,
        isClient: PropTypes.bool,
        isPractitioner: PropTypes.bool,
        isMobileOrTablet: PropTypes.bool,
        isReferrals: PropTypes.bool,
        offersHicaps: PropTypes.bool,
        offersTelehealth: PropTypes.bool.isRequired,
        practiceData: MobXPropTypes.arrayOrObservableArrayOf(
            PropTypes.shape({
                address: PropTypes.string,
                id: PropTypes.number,
                localityName: PropTypes.string,
                localityState: PropTypes.string,
                name: PropTypes.string,
                phones: MobXPropTypes.arrayOrObservableArrayOf(
                    PropTypes.shape({
                        number: PropTypes.string,
                    }),
                ).isRequired,
                practiceUrl: PropTypes.string,
            }),
        ),
        ratings: PropTypes.shape({
            average: PropTypes.number.isRequired,
            count: PropTypes.number.isRequired,
        }),
        renderModal: PropTypes.func,
        avatar: PropTypes.string,
        searchAvatar: PropTypes.string,
        specialInterestsTagline: PropTypes.string,
        specialties: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
            ),
            PropTypes.object, // ObservableArray
        ]),
        tagline: PropTypes.string.isRequired,
        tagline2: PropTypes.string.isRequired,
        telehealthDirectory: PropTypes.bool,
        url: PropTypes.string.isRequired,
        userType: PropTypes.string,
    };

    static defaultProps = {
        displayPhoneNumber: true,
    };

    getContactId(type) {
        const {id, practitioners} = this.props;
        if (type === PROFILE_CONTACT_TYPE) {
            return practitioners[0].id;
        } else {
            return id;
        }
    }

    constructor(props) {
        super(props);

        this.state = {
            modalDisplayed: false,
        };
        const practiceData = props.practiceData || [];
        const type = props.isPractitioner
            ? PROFILE_CONTACT_TYPE
            : PRACTICE_LOC_CONTACT_TYPE;
        this.modalProps = {
            closeModal: this.closeModal,
            contactId: this.getContactId(type),
            contactName: props.headline,
            contactType: type,
            isEnhanced: true,
            items: practiceData.map((item) => {
                // item will have type if it's a practice list that has a
                // practice group as the first element
                if (!item.type) {
                    item.type = type;
                }
                return item;
            }),
        };
    }

    @autobind
    closeModal() {
        this.setState({modalDisplayed: false});
    }

    @autobind
    handlePhoneClick() {
        this.setState({modalDisplayed: true});
    }

    @autobind
    trackBannerDetailEvent() {
        analytics.track('bannerDetail', {
            'useContent': false,
            'bannerAdId': this.props.id,
        });
    }

    getBookingsParam() {
        const {bannerType, practiceData, practitioners} = this.props;
        const param = {};
        let paramId;
        switch (bannerType) {
            case 'practice_group':
                if (practiceData.length > 0) {
                    paramId = practiceData[0]['id'];
                }
                param['practice_group'] = paramId;
                break;
            case 'practice_location':
                if (practiceData.length > 0) {
                    paramId = practiceData[0]['id'];
                }
                param['practice_location'] = paramId;
                break;
            case 'practitioners':
                if (practitioners.length > 0) {
                    paramId = practitioners[0]['id'];
                }
                param['practitioner'] = paramId;
                break;
        }
        return param;
    }

    getBannerUrl() {
        const {healthFund, url} = this.props;
        let bannerUrl = url;
        if (healthFund?.id) {
            bannerUrl += `?health_fund=${healthFund.id}`;
        }
        return bannerUrl;
    }

    getSpecialtyName(specialty) {
        const {
            rootStore: {
                paramStore: {isProUser, isReferrals},
            },
        } = this.context;
        if (isProUser || isReferrals) {
            return specialty.professionalName;
        } else {
            return specialty.name;
        }
    }

    renderSpecialties() {
        const {specialties} = this.props;
        if (!specialties.length) {
            return null;
        }
        return (
            <>
                {specialties
                    .map((specialty) => {
                        return this.getSpecialtyName(specialty);
                    })
                    .join(', ')}
            </>
        );
    }
    s;

    renderPhoneNumber() {
        const {headline, practiceData} = this.props;
        if (!practiceData || !practiceData[0]?.phones.length) {
            return null;
        }
        const phone = practiceData[0]?.phones[0];
        if (!phone.number) {
            return null;
        }
        return (
            <Chip
                customClass="chip-phone"
                icon="phone"
                key="chip-phone"
                linkComponent={
                    <CollapsibleNumber
                        customClass="expanded"
                        number={phone.number}
                        onClick={this.handlePhoneClick}
                        title={`Click to show phone number for ${headline}`}
                    />
                }
            />
        );
    }

    renderFees() {
        const {
            hasMedicalSpecialty,
            healthFund,
            id,
            isPractitioner,
        } = this.props;
        const iconColor = healthFund?.colorBrand
            ? {
                  color: `#${healthFund.colorBrand}`,
              }
            : null;

        if (isPractitioner && healthFund?.id && hasMedicalSpecialty) {
            return (
                <Chip
                    customClass="chip-cost fees-upsell"
                    icon="feesGraph"
                    iconColor={iconColor}
                    key="chip-cost"
                    linkComponent={
                        <Link href={`${this.getBannerUrl()}#fees`}>
                            {'See out-of-pocket costs'}
                        </Link>
                    }
                />
            );
        }
        return null;
    }

    getRatingAverage() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return null;
        }

        const {average} = ratings;
        return average;
    }

    getRatingCount() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {count} = ratings;
        return count;
    }

    getObjectType() {
        const {bannerType} = this.props;
        let objectTypeStr;
        switch (bannerType) {
            case 'practice_group':
                objectTypeStr = PRACTICE_GROUP_CARD;
                break;
            case 'practice_location':
                objectTypeStr = PRACTICE_LOCATION_CARD;
                break;
            case 'practitioners':
                objectTypeStr = PROFILE_CARD;
                break;
        }
        return objectTypeStr;
    }

    @autobind
    handleBookingAnchorAction(bookingId) {
        const element = document.getElementById(bookingId);
        if (element) {
            element.click();
        }
    }

    @autobind
    appendAnchor(bookingId) {
        const element = document.createElement('a');
        element.setAttribute('id', `${bookingId}`);
        element.setAttribute('class', '1stAvailableWidget2');
        element.setAttribute('data-mh1-source', 'healthshare');
        element.setAttribute('rel', 'nofollow noopener');
        document.body.appendChild(element);
    }

    renderBookingButton() {
        const {
            rootStore: {
                healthFundStore: {enabledMH1BookingIntegration},
                paramStore: {hasSkin},
            },
        } = this.context;
        if (hasSkin) {
            return null;
        }
        const {
            isClient,
            bookingsEnabled,
            customBookingDataUrl,
            id,
        } = this.props;

        const bookingId = enabledMH1BookingIntegration(this.props)?.bookingId;
        if (bookingId) {
            this.appendAnchor(bookingId);
            myHealthFirst.widgetSetup();

            return (
                <div className="hs-button-container">
                    <BookNowButton
                        action={() =>
                            this.handleBookingAnchorAction(bookingId)
                        }
                        gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                        gaSelector={BOOK_NOW_BTN_MH1_PRIMARY}
                        objectId={id}
                        objectType="directory result"
                        params={{practitioner: id}}
                    />
                </div>
            );
        }

        if (!isClient && bookingsEnabled?.length > 0) {
            return (
                <div className="hs-button-container">
                    <BookNowButton
                        gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                        objectId={id}
                        objectType="banner ad"
                        params={this.getBookingsParam()}
                    />
                </div>
            );
        }

        if (customBookingDataUrl) {
            return (
                <div className="hs-button-container">
                    <BookNowButton
                        action={() => openLinkInNewTab(customBookingDataUrl)}
                        gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                        objectType="banner ad"
                        objectId={id}
                        params={this.getBookingsParam()}
                    />
                </div>
            );
        }
    }

    subtitleBlock() {
        const {showNextAvailableGp} = this.context;

        if (!showNextAvailableGp) {
            return;
        }

        const {bookingIntegration} = this.props;
        const maybeAvail = this.context.availabilities[
            bookingIntegration?.bookingId
        ];

        if (!bookingIntegration || !maybeAvail) {
            return;
        }

        const formatted = getBookingAvailability(
            [maybeAvail],
            bookingIntegration,
        );

        if (!formatted) {
            return;
        }

        return (
            <IntegratedAvailabilityCallout
                newPatientsOnly
                formattedDate={formatted.nextAvailableTimeShort}
                gaObjectType="banner-ad-card profile-card"
                gaSelector="new-patient-next-available-only"
            />
        );
    }

    renderMainContent() {
        const {
            avatar,
            bookingsEnabled,
            bookingIntegration,
            customBookingDataUrl,
            displayPhoneNumber,
            hasMedicalSpecialty,
            headline,
            healthFund,
            id,
            initials,
            isClient,
            isMobileOrTablet,
            isPractitioner,
            isStaff,
            offersHicaps,
            offersTelehealth,
            searchAvatar,
            telehealthDirectory,
            tagline,
            tagline2,
            specialInterestsTagline,
            practiceData,
        } = this.props;

        const directoryCardProps = {
            avatarInitials: initials && initials,
            avatarSizeTablet: isClient
                ? AVATAR_SIZE_DEFAULT
                : AVATAR_SIZE_LARGE,
            avatarSource: (searchAvatar || avatar) && (searchAvatar || avatar),
            bookingModule: this.renderBookingButton(),
            chips: [
                specialInterestsTagline && (
                    <Chip
                        concatenationText=" "
                        concatenationWidth={isMobileOrTablet ? 100 : 150}
                        customClass={'chip-special-interests'}
                        hasConcatenation
                        icon="practitioner"
                        key="chip-special-interests"
                        text={specialInterestsTagline}
                        title="Special interests:"
                    />
                ),
                practiceData && practiceData[0]?.address && (
                    <Chip
                        customClass={'chip-address'}
                        icon="address"
                        key="chip-address"
                        text={practiceData[0]?.address}
                    />
                ),
                ((isPractitioner && healthFund?.id && hasMedicalSpecialty) ||
                    displayPhoneNumber) &&
                    displayPhoneNumber &&
                    this.renderPhoneNumber(),
                this.renderFees(),
            ],
            dataTestId: 'bannerAd',
            description: (tagline || tagline2) && (
                <span data-test-id="taglines">
                    {tagline && tagline}
                    {tagline && tagline[tagline.length - 1] === '.'
                        ? ' '
                        : '. '}
                    {tagline2 && tagline2}
                </span>
            ),
            heading: headline,
            isFeatured: true,
            isIntegrated: isClient,
            subtitleBlock: this.subtitleBlock(),
            pills: (telehealthDirectory ||
                offersTelehealth ||
                offersHicaps) && [
                offersTelehealth && (
                    <Pill
                        key="telehealth"
                        text={TELEHEALTH}
                        tooltip={
                            browserCheckForIE11()
                                ? null
                                : TOOLTIP_TELEHEALTH_TEXT
                        }
                    />
                ),
                offersHicaps && (
                    <Pill
                        key="hicaps"
                        text={HICAPS}
                        tooltip={
                            browserCheckForIE11() ? null : TOOLTIP_HICAPS_TEXT
                        }
                    />
                ),
            ],
            ratingAverage: this.getRatingAverage(),
            ratingCount: this.getRatingCount(),
            size: 'default',
            subHeading: this.renderSpecialties(),
            tooltipText: browserCheckForIE11() ? null : TOOLTIP_FEATURED_TEXT,
        };

        return (
            <>
                <Link
                    action={this.trackBannerDetailEvent}
                    customClass="card-link"
                    gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                    gaSelector={DIRECTORY_SEARCH_RESULT_CARD_LINK}
                    href={this.getBannerUrl()}
                >
                    <span>{`${headline} profile link`}</span>
                </Link>
                <Card {...directoryCardProps} />
            </>
        );
    }

    render() {
        const {renderModal, telehealthDirectory} = this.props;
        return (
            <li
                className={`cell ${
                    telehealthDirectory ? 'telehealth-directory' : ''
                }`.trim()}
            >
                {this.state.modalDisplayed && renderModal(this.modalProps)}
                {this.renderMainContent()}
            </li>
        );
    }
}
